import { CaretRightOutlined } from '@ant-design/icons'
import { Spin } from 'antd'
import classNames from 'classnames'
import React, {
  MouseEventHandler,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react'
import videojs from 'video.js'

import {
  Media,
  MediaStatus,
  MediaType,
} from '@/model/media'

import s from './media-view.module.css'
import 'video.js/dist/video-js.min.css'

export interface MediaProps {
  media: Media
  isPreview?: boolean
  onClick?: (media: Media) => void
  className?: string
  extra?: ReactNode
}

interface MediaContentProps {
  type: MediaType
  src?: string
  isPreview?: boolean
}

export function MediaContent({
  type,
  src,
  isPreview,
}: MediaContentProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const splitSrc = src?.split(',') || []
  const previewSrc = splitSrc.length > 1 ? splitSrc[0] : src
  const sourceSrc = splitSrc.length > 1 ? splitSrc[1] : src

  useEffect(() => {
    if (!videoRef.current) {
      return
    }

    const player = videojs(videoRef.current, {
      sources: [{
        src: sourceSrc,
        type: 'application/x-mpegURL',
      }],
      controls: true,
      autoplay: false,
      preload: 'auto',
    })

    return () => {
      player.dispose()
    }
  }, [sourceSrc])

  if (!sourceSrc) return null

  if (!isPreview && (type === MediaType.Video || type === MediaType.GIF)) {
    return sourceSrc.endsWith('m3u8') ? (
      <>
        <video
          className={classNames(s.VideoShow, 'video-js vjs-default-skin')}
          ref={videoRef}
        >
          <source type="application/x-mpegURL" />
        </video>
      </>
    ) : (
      <video className={s.VideoShow} autoPlay={false} controls>
        <source src={sourceSrc} type="video/mp4" />
      </video>
    )
  }
  if (!isPreview && type === MediaType.Audio) {
    return (
      <audio controls>
        <source src={src} type="audio/mp3" />
      </audio>
    )
  }
  if (isPreview && type === MediaType.Audio) {
    return <CaretRightOutlined className={s.Play} />
  }
  if (isPreview && (type === MediaType.Video || type === MediaType.GIF)) {
    if (type === MediaType.GIF) {
      return (
        <>
          <img referrerPolicy={'no-referrer'} src={previewSrc === '' ? src : previewSrc} />
          <CaretRightOutlined className={s.Play} />
        </>
      )
    }
    return (
      <>
        <img referrerPolicy={'no-referrer'} src={previewSrc} />
        <CaretRightOutlined className={s.Play} />
      </>
    )
  }
  return <img referrerPolicy={'no-referrer'} src={src} />
}

export default function MediaView({
  media,
  media: {
    src,
    type,
  },
  onClick,
  className,
  extra,
  isPreview,
}: MediaProps) {
  const click: MouseEventHandler<HTMLDivElement> = useCallback((event) => {
    event.stopPropagation()
    if (onClick) {
      onClick(media)
    }
  }, [media, onClick])

  if (media.status === MediaStatus.Pending) {
    return (
      <div className={classNames(className, s.Media, { [s.isPreview]: isPreview })}>
        <Spin spinning />
      </div>
    )
  }

  return (
    <div
      className={classNames(className, s.Media, { [s.isPreview]: isPreview })}
      onClick={click}
    >
      <MediaContent type={type} src={src} isPreview={isPreview} />
      {extra}
    </div>
  )
}
